<template>
  <div class="pmpe-container">
    <DxPopup
      :visible="visible"
      title="Select Media"
      :width="'75%'"
      :height="680"
      :drag-enabled="false"
      :hide-on-outside-click="false"
      :show-close-button="false"
      :wrapperAttr="{ class: 'css-v2 gc-popup gc-popup-product-media-pick-existing' }"
      @hidden="onClose"
    >
      <DxToolbarItem
        v-for="(item, index) in getToolbarItems"
        :key="index"
        :widget="item.widget"
        :location="item.location"
        :toolbar="item.position"
        :visible="item.visible"
        :options="item.options"
      />

      <div class="css-v2 transaction-list">
        <gc-filter-split-view
          :showLeftContainer="showLeftContainer"
          :showRightContainer="showRightContainer"
          :rightContainerWidth="260"
        >
          <template #leftContent>
            <DxDataGrid
              :data-source="files"
              :show-column-headers="true"
              :column-chooser="{ enabled: false }"
              :width="'100%'"
              :height="getGridHeight"
              key-expr="RecordID"
              :selection="{ mode: 'multiple', showCheckBoxesMode: 'always' }"
              :selected-row-keys.sync="selectedRowKeys"
              @toolbar-preparing="onToolbarPreparing"
              @content-ready="onContentReady"
            >
              <DxScrolling mode="virtual" :row-rendering-mode="'virtual'" :preload-enabled="true" />
              <DxPaging :enabled="false" />
              <DxPager :visible="false" />

              <DxColumn
                caption=""
                :allow-hiding="false"
                :allow-sorting="false"
                :width="70"
                :cell-template="previewCellTemplate"
              />
              <DxColumn
                data-field="FileName"
                caption="File Name"
                :allow-hiding="false"
                :force-width="true"
                :width="ColumnWidth.ShortDescription.width"
              />
              <DxColumn
                data-field="ContentType"
                caption="File Type"
                :width="ColumnWidth.Status.width"
                :calculate-cell-value="fileTypeLabel"
              />
              <DxColumn
                data-field="DateCreated"
                caption="Uploaded"
                data-type="date"
                format="dd MMM yyyy"
                :width="ColumnWidth.Date.width"
              />
              <DxColumn
                data-field="Products"
                caption="Product"
                :width="ColumnWidth.ProductCode.width"
                :calculate-cell-value="productCodesLabel"
              />
            </DxDataGrid>
          </template>

          <template #rightContent>
            <div class="advance-filter-panel">
              <DxToolbar class="split-view-toolbar">
                <DxItem location="before" widget="dxTabs" :options="filterPanelTabOptions" />
                <DxItem :options="filterCloseToolbarOptions" location="after" widget="dxButton" />
              </DxToolbar>

              <div class="view-card responsive-paddings">
                <div class="advance-filter-header-btns">
                  <DxButton
                    class="apply-btn"
                    text="Refresh"
                    type="default"
                    @click="onSearch"
                  />
                  <DxButton text="reset" type="normal" @click="onReset" />
                </div>

                <div class="pmpe-filter-field">
                  <span class="pmpe-filter-label">Search</span>
                  <DxTextBox
                    :value="filters.search"
                    placeholder="Search file name"
                    @value-changed="filters.search = $event.value"
                  />
                </div>

                <div class="pmpe-filter-field">
                  <span class="pmpe-filter-label">Product</span>
                  <gc-field-tag
                    :key="'pmpe-product-' + filterResetKey"
                    :tagBoxOptions="productTagBoxOptions"
                    :dataModel="filters"
                  />
                </div>

                <div class="pmpe-filter-field">
                  <span class="pmpe-filter-label">File Type</span>
                  <DxSelectBox
                    :value="filters.contentType"
                    :data-source="fileTypeOptions"
                    display-expr="label"
                    value-expr="value"
                    :show-clear-button="true"
                    placeholder="All types"
                    @value-changed="filters.contentType = $event.value"
                  />
                </div>

                <div class="pmpe-filter-field">
                  <span class="pmpe-filter-label">Uploaded</span>
                  <gc-field-date
                    :key="'pmpe-uploaded-' + filterResetKey"
                    :dateBoxOptions="uploadedDateBoxOptions"
                    :valueChangeCallBack="onUploadedFilterChange"
                  />
                </div>
              </div>
            </div>
          </template>
        </gc-filter-split-view>

        <DxLoadPanel
          :position="{ of: '#container-wrapper' }"
          :visible.sync="loadingVisible"
          :show-indicator="true"
          :show-pane="true"
          :shading="true"
          :hide-on-outside-click="false"
          shading-color="rgba(0,0,0,0.3)"
        />
      </div>
    </DxPopup>

    <gc-popup-image-preview
      v-if="previewVisible"
      :data-model="previewDataModel"
      :content-type="previewFile && getOriginalContentType(previewFile)"
      :preview-visible="previewVisible"
      :on-back-button-click="closePreview"
      :allow-download="true"
      :allow-remove="false"
    />
  </div>
</template>

<script>
import {
  ProductMediaService,
  isImageFile,
  getThumbUrl,
  getPreviewUrl,
  getOriginalContentType,
} from "../../_services/api/products/product-media.service";
import ColumnWidth from "../../_helpers/data-grid/ColumnWidth";
import gcPopupImagePreview from "../gc-popup-image-preview.vue";
import appConfig from "../../app-config";

const FILE_TYPE_OPTIONS = [
  { value: "image/jpeg", label: "Image (.jpg)" },
  { value: "image/png", label: "Image (.png)" },
  { value: "application/pdf", label: "PDF" },
  { value: "text/plain", label: "Text" },
  { value: "application/msword", label: "Word (.doc)" },
  { value: "application/vnd.openxmlformats-officedocument.wordprocessingml.document", label: "Word (.docx)" },
  { value: "application/vnd.ms-excel", label: "Excel (.xls)" },
  { value: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet", label: "Excel (.xlsx)" },
  { value: "text/csv", label: "CSV" },
];

export default {
  components: { "gc-popup-image-preview": gcPopupImagePreview },
  props: {
    visible: {
      type: Boolean,
      default: false,
    },
    productCode: {
      type: String,
      default: null,
    },
    displayServerError: {
      type: Function,
      default: () => {},
    },
  },
  data() {
    return {
      ColumnWidth,
      files: [],
      hasSearched: false,
      fileTypeOptions: FILE_TYPE_OPTIONS,
      selectedRowKeys: [],
      previewVisible: false,
      previewFile: null,
      loadingVisible: false,
      showLeftContainer: true,
      showRightContainer: true,
      filterResetKey: 0,
      gridHeight: "auto",
      filterCloseToolbarOptions: {
        icon: "showpanel",
        onClick: () => {
          this.showRightContainer = !this.showRightContainer;
        },
      },
      filters: {
        search: "",
        productCodes: [],
        contentType: null,
        uploadedFilter: [],
      },
    };
  },
  created() {
    setTimeout(() => {
      const el = document.querySelectorAll(
        ".gc-popup-product-media-pick-existing .dx-popup-content",
      )[0];
      if (el) {
        this.gridHeight = el.clientHeight - 20;
      }
    }, 1);
  },
  methods: {
    getPreviewUrl,
    getOriginalContentType,
    onClose() {
      this.selectedRowKeys = [];
      this.$emit("close");
    },
    onContentReady(e) {
      var noData = e.element.querySelector(".dx-datagrid-nodata:not(.dx-hidden)");
      if (!noData) return;

      noData.innerHTML = this.hasSearched
        ? "<div>Uh-oh! We are struggling to find what you are looking for. Try altering your search criteria.</div>"
        : "<div>Enter a search term or choose a filter to see files.</div>";
    },
    async onSearch() {
      var hasAnyFilter =
        !!this.filters.search ||
        (this.filters.productCodes && this.filters.productCodes.length > 0) ||
        !!this.filters.contentType ||
        (this.filters.uploadedFilter && this.filters.uploadedFilter.length > 0);

      if (!hasAnyFilter) {
        this.files = [];
        this.hasSearched = false;
        return;
      }

      this.hasSearched = true;
      this.loadingVisible = true;

      await ProductMediaService.searchFiles({
        search: this.filters.search,
        contentType: this.filters.contentType,
        productCodes: this.filters.productCodes,
        uploadedFilter: this.filters.uploadedFilter,
      })
        .then((response) => {
          this.files = response.data.value;
        })
        .finally(() => {
          this.loadingVisible = false;
        });
    },
    onReset() {
      this.filters = {
        search: "",
        productCodes: [],
        contentType: null,
        uploadedFilter: [],
      };
      this.filterResetKey += 1;
      this.files = [];
      this.hasSearched = false;
    },
    onUploadedFilterChange(dataField, filter) {
      this.filters.uploadedFilter = filter && filter.length > 0 ? filter : [];
    },
    onToolbarPreparing(e) {
      e.toolbarOptions.items.push({
        location: "after",
        locateInMenu: "auto",
        widget: "dxButton",
        options: {
          icon: "filter",
          onClick: () => {
            this.showRightContainer = !this.showRightContainer;
          },
          visible: !this.showRightContainer,
        },
      });
    },
    fileTypeLabel(rowData) {
      var contentType = getOriginalContentType(rowData);
      var match = FILE_TYPE_OPTIONS.find((o) => o.value === contentType);
      return match ? match.label : contentType;
    },
    productCodesLabel(rowData) {
      if (!rowData.ProductMediaAttachments || rowData.ProductMediaAttachments.length === 0) {
        return "";
      }
      return rowData.ProductMediaAttachments.map((a) => a.ProductCode).join(", ");
    },
    previewCellTemplate(container, info) {
      var element;
      if (isImageFile(info.data)) {
        element = document.createElement("img");
        element.src = getThumbUrl(info.data);
        element.alt = info.data.FileName;
        element.className = "pmpe-preview-thumb";
      } else {
        element = document.createElement("i");
        element.className = "dx-icon dx-icon-find pmpe-preview-icon";
      }
      element.title = "Preview";
      element.addEventListener("click", (e) => {
        e.stopPropagation();
        this.previewFile = info.data;
        this.previewVisible = true;
      });
      container.appendChild(element);
    },
    closePreview() {
      this.previewVisible = false;
      this.previewFile = null;
    },
    async onSelectClick() {
      if (!this.selectedRowKeys || this.selectedRowKeys.length === 0) return;

      await ProductMediaService.associateFiles({
        ProductCode: this.productCode,
        ProductMediaFileRecordIDs: this.selectedRowKeys,
      })
        .then(() => {
          this.$emit("selected");
          this.onClose();
        })
        .catch((err) => {
          this.displayServerError(err, "Select Failed");
        });
    },
  },
  computed: {
    getGridHeight() {
      return this.gridHeight;
    },
    previewDataModel() {
      if (!this.previewFile) return {};
      return {
        PreviewImageSrc: getPreviewUrl(this.previewFile),
        FileName: this.previewFile.FileName,
        DownloadApiUrl: `${appConfig.VUE_APP_ROOT_API}/api/ProductMediaFile/${this.previewFile.RecordID}/Download?sizeType=original`,
      };
    },
    getToolbarItems() {
      return [
        {
          widget: "dxButton",
          location: "after",
          position: "top",
          visible: true,
          options: {
            text: "Select",
            stylingMode: "contained",
            type: "success",
            disabled: this.selectedRowKeys.length === 0,
            onClick: this.onSelectClick,
          },
        },
        {
          widget: "dxButton",
          location: "after",
          position: "top",
          visible: true,
          options: {
            text: "Cancel",
            stylingMode: "outlined",
            type: "danger",
            onClick: this.onClose,
          },
        },
      ];
    },
    filterPanelTabOptions() {
      return {
        dataSource: [{ id: 0, text: "Filters" }],
        selectedIndex: 0,
      };
    },
    productTagBoxOptions() {
      return [
        {
          dataField: "productCodes",
          valueExpr: "Code",
          displayExpr: (item) => (item ? `${item.Code} - ${item.Description1 || ""}` : ""),
          searchExpr: ["Code", "Description1"],
          editorTemplate: {
            primary: [{ dataField: "Description1" }],
            secondary: [{ dataField: "Code" }],
          },
          dataSourceConfig: {
            endpoint: "api/Product",
            select: ["Code", "Description1"],
            filter: ["Inactive", "=", "N"],
          },
        },
      ];
    },
    uploadedDateBoxOptions() {
      return [
        {
          dataField: "DateCreated",
          label: { text: "Uploaded" },
        },
      ];
    },
  },
};
</script>

<style lang="scss">
.gc-popup-product-media-pick-existing {
  .dx-popup-content {
    padding: 0;
  }

  .pmpe-empty {
    padding: 24px;
    text-align: center;
    color: #98a2b3;
    font-size: 12px;
  }

  .pmpe-preview-icon {
    cursor: pointer;
    color: #667085;

    &:hover {
      color: #35baf6;
    }
  }

  .pmpe-preview-thumb {
    width: 32px;
    height: 32px;
    object-fit: cover;
    border-radius: 4px;
    cursor: pointer;
    display: block;
  }

  .advance-filter-panel {
    height: 100%;
    display: flex;
    flex-direction: column;

    .view-card.responsive-paddings {
      flex: 1;
      overflow-y: auto;
    }
  }

  .pmpe-filter-field {
    margin-bottom: 16px;

    .pmpe-filter-label {
      display: block;
      font-size: 11px;
      color: #667085;
      margin-bottom: 4px;
    }
  }
}
</style>
